// 注册 oas-virtual-list（OASVirtualList 仅作类型用，需裸 import 保住注册副作用）
import '../../data/virtual-list/index.js'
import type { OASVirtualList } from '../../data/virtual-list/index.js'
// 注册 oas-bottom-sheet（移动端底部抽屉承载件，需裸 import 保住注册副作用）
import '../../feedback/bottom-sheet/index.js'
import type { OASBottomSheet } from '../../feedback/bottom-sheet/index.js'
import { watchMobileSheetMode } from '../../shared/mobile-sheet.js'
import { computePosition, getViewport, type Placement } from '../../overlay/floating/index.js'
import { resolveDirection } from '../../shared/direction.js'
import { measureMaxTextWidth } from '../../shared/measure-text.js'
import { cssVarPx } from '../../shared/css-var.js'
import { TOUCH_TARGET_CSS } from '../../shared/touch-target.js'
import { OASFormElement } from '@oas-ui/core'

export interface Option {
  label: string
  value: string
  disabled?: boolean
  /** 选项分组标题：同一组连续渲染组标题（不可选），组内选项缩进 */
  group?: string
}

/** size 尺寸档（对齐 oas-input：small/medium/large，控高走 --oas-control-height-* token） */
const VALID_SIZES = ['small', 'medium', 'large'] as const
/** variant 形态（对齐 oas-input 三值枚举：outlined 默认 / filled 填充 / borderless 无框） */
const VALID_VARIANTS = ['outlined', 'filled', 'borderless'] as const
/** status 校验态：success / warning / error（error 联动 aria-invalid） */
const VALID_STATUSES = ['error', 'warning', 'success'] as const

/** 枚举归一化：合法值原样返回，空/非法值静默回落默认（非法值不告警，保持输出干净） */
function normalizeChoice(raw: string, fallback: string, valid: readonly string[]): string {
  if (raw === '') return fallback
  return (valid as readonly string[]).includes(raw) ? raw : fallback
}

/** 选项行样式（非虚拟模式渲染在 select 自身 shadow；虚拟模式需注入到 vlist shadow，两处共用） */
const OPTION_STYLE = `
.option {
  padding: var(--oas-space-2) var(--oas-space-3);
  border-radius: var(--oas-radius-sm);
  cursor: pointer;
  font-size: var(--oas-font-size-md);
  color: var(--oas-color-text-primary);
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.option:hover {
  background: var(--oas-color-bg-hover);
}
.option.active {
  background: var(--oas-color-primary);
  color: var(--oas-color-text-on-primary);
}
.option.grouped {
  padding-inline-start: calc(var(--oas-space-3) + var(--oas-space-4));
}
.option[aria-disabled='true'] {
  cursor: not-allowed;
  opacity: 0.5;
}
.option .check {
  visibility: hidden;
}
.option[aria-selected='true'] .check {
  visibility: visible;
}
.option-label {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: var(--oas-space-2);
}
.option-label > * {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
`

/** 虚拟模式注入 oas-virtual-list 的 shadow：选项行占满 item、整行可高亮 */
const VIRTUAL_ROW_STYLE = `
[part="item"] {
  display: flex;
  align-items: center;
}
[part="item"] .option {
  flex: 1;
  height: 100%;
  box-sizing: border-box;
}
${OPTION_STYLE}
`

const STYLE = `
 :host {
  display: inline-block;
  position: relative;
  font-family: inherit;
  width: 220px;
  /* 尺寸档内部控高变量（data-size 镜像切换；不占公开 API，外部请用 size 属性） */
  --_ch: var(--oas-control-height-md);
  /* 下拉高度 CSS 变量开口：宿主覆盖即可调高（默认 240px），不占属性 API */
  --oas-select-dropdown-height: 240px;
  /* reserve-width 锁宽通道：JS 写量测值，缺省 0px 无副作用（不占宿主盒） */
  min-width: var(--oas-select-reserve, 0px);
}
:host([hidden]) {
  display: none;
}
:host([data-size='small']) {
  --_ch: var(--oas-control-height-sm);
}
:host([data-size='large']) {
  --_ch: var(--oas-control-height-lg);
}
.trigger {
  appearance: none;
  box-sizing: border-box;
  width: 100%;
  min-height: var(--_ch);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--oas-space-2);
  padding: 0 var(--oas-space-3);
  border: 1px solid var(--oas-color-border);
  /* compact/button-group 圆角合并协议：--oas-button-group-radius 优先，独立使用回落自身圆角 */
  border-radius: var(--oas-button-group-radius, var(--oas-radius-md));
  background: var(--oas-color-bg);
  color: var(--oas-color-text-primary);
  font-size: var(--oas-font-size-md);
  font-family: inherit;
  cursor: pointer;
  transition: border-color var(--oas-transition-fast) var(--oas-ease-out),
    box-shadow var(--oas-transition-fast) var(--oas-ease-out);
}
/* ---- size 尺寸档：字号/padding/chip 控高联动（默认 medium 走基础样式） ---- */
:host([data-size='small']) .trigger {
  font-size: var(--oas-font-size-sm);
  padding: 0 var(--oas-space-2);
}
:host([data-size='large']) .trigger {
  font-size: var(--oas-font-size-lg);
  padding: 0 var(--oas-space-4);
}
:host([data-size='small']) :is(.chip, .chip-plus) {
  height: 16px;
}
:host([data-size='large']) :is(.chip, .chip-plus) {
  height: 26px;
}
:host([data-size='small']) .search-input {
  font-size: var(--oas-font-size-sm);
}
:host([data-size='large']) .search-input {
  font-size: var(--oas-font-size-lg);
}
/* ---- variant 形态：filled 填充 / borderless 无框（默认 outlined 走基础样式）。
     置于 status 规则之前：同特异性时后定义的 status 语义色胜出（对齐 oas-input 排序） ---- */
:host([data-variant='filled']) .trigger {
  border-color: transparent;
  background: var(--oas-color-bg-hover);
}
:host([data-variant='filled']) .trigger:hover {
  border-color: var(--oas-color-border);
}
:host([data-variant='filled']) .trigger:focus-visible,
:host([data-variant='filled']) .trigger[aria-expanded='true'] {
  border-color: var(--oas-color-primary);
  box-shadow: var(--oas-focus-ring);
}
:host([data-variant='filled']) .trigger[disabled] {
  background: var(--oas-color-bg-disabled);
}
:host([data-variant='borderless']) .trigger {
  border-color: transparent;
  background: transparent;
}
:host([data-variant='borderless']) .trigger:hover {
  border-color: transparent;
}
:host([data-variant='borderless']) .trigger:focus-visible,
:host([data-variant='borderless']) .trigger[aria-expanded='true'] {
  border-color: transparent;
  box-shadow: none;
}
:host([data-variant='borderless']) .trigger[disabled] {
  background: transparent;
}
.trigger:hover {
  border-color: var(--oas-color-primary);
}
.trigger:focus-visible {
  outline: none;
  box-shadow: var(--oas-focus-ring);
}
.trigger[aria-expanded='true'] {
  border-color: var(--oas-color-primary);
}
.trigger[aria-readonly='true'] {
  /* readonly：可聚焦可复制但不弹层——光标不指示可点开 */
  cursor: default;
}
/* ---- status 校验态：success / warning / error（宿主自设 aria-invalid 等效 error 视觉，置于此处统一胜出） ---- */
:host([data-status='success']) .trigger {
  border-color: var(--oas-color-success);
}
:host([data-status='success']) .trigger:focus-visible,
:host([data-status='success']) .trigger[aria-expanded='true'] {
  border-color: var(--oas-color-success);
  box-shadow: 0 0 0 2px color-mix(in srgb, var(--oas-color-success) 30%, transparent);
}
:host([data-status='warning']) .trigger {
  border-color: var(--oas-color-warning);
}
:host([data-status='warning']) .trigger:focus-visible,
:host([data-status='warning']) .trigger[aria-expanded='true'] {
  border-color: var(--oas-color-warning);
  box-shadow: 0 0 0 2px color-mix(in srgb, var(--oas-color-warning) 30%, transparent);
}
:host([data-status='error']) .trigger,
:host([aria-invalid='true']) .trigger {
  border-color: var(--oas-color-danger);
}
:host([data-status='error']) .trigger:focus-visible,
:host([data-status='error']) .trigger[aria-expanded='true'],
:host([aria-invalid='true']) .trigger:focus-visible,
:host([aria-invalid='true']) .trigger[aria-expanded='true'] {
  border-color: var(--oas-color-danger);
  box-shadow: 0 0 0 2px color-mix(in srgb, var(--oas-color-danger) 30%, transparent);
}
.trigger[disabled] {
  cursor: not-allowed;
  background: var(--oas-color-bg-disabled);
  color: var(--oas-color-text-disabled);
}
.value {
  display: flex;
  flex-wrap: wrap;
  gap: var(--oas-space-1);
  min-width: 0;
  flex: 1;
  text-align: start;
  /* 默认换行：标签放不下时换行、触发器自适应增高；折叠仅在显式设置 max-tag-count 时启用 */
}
:host([max-tag-count]) .value {
  flex-wrap: nowrap;
  /* max-tag-count 折叠模式：单行不换行、不出横向滚动条，放不下的标签折叠为 +N（见 collapseOverflowChips） */
  overflow: hidden;
}
.placeholder {
  color: var(--oas-color-text-secondary);
}
.chip {
  display: inline-flex;
  align-items: center;
  box-sizing: border-box;
  height: 20px;
  flex-shrink: 0;
  max-width: 100%;
  gap: var(--oas-space-1);
  background: var(--oas-color-bg-hover);
  border-radius: var(--oas-radius-sm);
  padding: 0 var(--oas-space-1);
  font-size: var(--oas-font-size-xs);
  color: var(--oas-color-text-primary);
}
/* 超长标签在 chip 内省略，避免超出容器横向溢出（换行模式下单个标签无法再折行） */
.chip > span:first-child {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
}
/* 自定义标签模板（template[slot="tag"]）渲染时：标签容器改为 flex 布局，子元素各自省略 */
.chip .chip-label {
  display: inline-flex;
  align-items: center;
  gap: var(--oas-space-1);
  min-width: 0;
}
.chip[hidden] {
  display: none;
}
/* 折叠计数 chip：样式同 .chip，颜色次要；仅在需要折叠时才插入 DOM */
.chip-plus {
  display: inline-flex;
  align-items: center;
  box-sizing: border-box;
  height: 20px;
  flex-shrink: 0;
  gap: var(--oas-space-1);
  background: var(--oas-color-bg-hover);
  border-radius: var(--oas-radius-sm);
  padding: 0 var(--oas-space-1);
  font-size: var(--oas-font-size-xs);
  color: var(--oas-color-text-secondary);
}
.chip-plus[hidden] {
  display: none;
}
.chip button {
  appearance: none;
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 0 2px;
  color: var(--oas-color-text-secondary);
  font-size: 1em;
  line-height: 1;
}
.chevron {
  transition: transform var(--oas-transition-fast) var(--oas-ease-out);
  /* 多行标签时箭头固定首行对齐，不随触发器长高漂浮（随尺寸档控高联动） */
  align-self: flex-start;
  margin-top: calc((var(--_ch) - 12px) / 2);
  flex-shrink: 0;
}
.trigger[aria-expanded='true'] .chevron {
  transform: rotate(180deg);
}
.clear-btn {
  appearance: none;
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 0 2px;
  margin: 0;
  color: var(--oas-color-text-secondary);
  font-size: 1em;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  border-radius: var(--oas-radius-sm);
  flex-shrink: 0;
  /* 多行标签时清除按钮固定首行对齐，不随触发器长高漂浮（与 .chevron 一致，随尺寸档联动） */
  align-self: flex-start;
  margin-top: calc((var(--_ch) - 12px) / 2);
}
.clear-btn:hover {
  color: var(--oas-color-text-primary);
}
.clear-btn:focus-visible {
  outline: none;
  box-shadow: var(--oas-focus-ring);
}
.clear-btn[hidden] {
  display: none;
}
.clear-btn svg {
  width: 12px;
  height: 12px;
  display: block;
}
/* 自定义清空图标（template[slot="clear-icon"]）在场时隐藏默认 × svg（svg 无 hidden IDL，走 attribute） */
.clear-btn svg[hidden] {
  display: none;
}
.clear-icon {
  display: inline-flex;
  align-items: center;
}
.clear-icon[hidden] {
  display: none;
}
/* ---- 触发器前后缀与自定义后缀图标（template[slot=prefix/suffix/suffix-icon] 克隆目标） ---- */
.prefix,
.suffix {
  display: inline-flex;
  align-items: center;
  color: var(--oas-color-text-secondary);
  flex: none;
}
.prefix[hidden],
.suffix[hidden] {
  display: none;
}
.suffix-icon {
  display: inline-flex;
  align-items: center;
  color: var(--oas-color-text-secondary);
  flex-shrink: 0;
  transition: transform var(--oas-transition-fast) var(--oas-ease-out);
  /* 多行标签时固定首行对齐（与 .chevron 一致，随尺寸档控高联动） */
  align-self: flex-start;
  margin-top: calc((var(--_ch) - 12px) / 2);
}
.suffix-icon[hidden] {
  display: none;
}
.chevron[hidden] {
  display: none;
}
/* 自定义后缀图标替换 chevron 后，展开旋转样式同容器生效 */
.trigger[aria-expanded='true'] .suffix-icon {
  transform: rotate(180deg);
}
/* ---- 触发器下方提示文案（hint 属性，aria-describedby 关联） ---- */
.hint {
  font-size: var(--oas-font-size-sm);
  color: var(--oas-color-text-secondary);
  margin-top: var(--oas-space-1);
  line-height: 1.5;
}
.hint[hidden] {
  display: none;
}
/* ---- auto-width：触发器宽度随选中项收缩（host 让出固定宽度，行内收缩适配内容） ---- */
:host([auto-width]) {
  width: auto;
}
.dropdown {
  /* fixed + computePosition 锚定 trigger 下方：逃出祖先 overflow 容器（模态滚动 body 等），
     不再为该容器贡献溢出逼出滚动条（与 combobox 同思路，见 oas-select 定位契约） */
  position: fixed;
  z-index: calc(var(--oas-z-index-base, 0) + var(--oas-z-dropdown, 1000));
  background: var(--oas-color-bg);
  border: 1px solid var(--oas-color-border);
  border-radius: var(--oas-radius-md);
  box-shadow: var(--oas-shadow-md);
  /* 液态玻璃接线（容器级 surface）：变量缺省时零影响 */
  backdrop-filter: var(--oas-glass-blur, none);
  -webkit-backdrop-filter: var(--oas-glass-blur, none);
  outline: 1px solid var(--oas-glass-ring, transparent);
  outline-offset: -1px;
  padding: var(--oas-space-1);
  display: none;
}
.dropdown.open {
  display: block;
}
/* 移动形态（data-mobile-sheet）：bottom-sheet 底部抽屉承载，dropdown 变静态内嵌内容
   （容器/手势/安全区由 oas-bottom-sheet 统一承载，dropdown 不再 fixed 锚定） */
:host([data-mobile-sheet]) .dropdown {
  position: static;
  z-index: auto;
  border: none;
  box-shadow: none;
  border-radius: 0;
  padding: 0;
  width: auto !important;
  top: auto !important;
  left: auto !important;
}
.search-input {
  box-sizing: border-box;
  width: 100%;
  height: var(--_ch);
  margin-bottom: var(--oas-space-1);
  padding: 0 var(--oas-space-2);
  border: 1px solid var(--oas-color-border);
  border-radius: var(--oas-radius-sm);
  font-size: var(--oas-font-size-md);
  font-family: inherit;
  background: var(--oas-color-bg);
  color: var(--oas-color-text-primary);
}
.search-input:focus {
  outline: none;
  border-color: var(--oas-color-primary);
  box-shadow: var(--oas-focus-ring);
}
/* ---- 下拉头尾插槽容器（template[slot="header"/"footer"] 克隆目标；键盘焦点不进入） ---- */
.dropdown-header,
.dropdown-footer {
  padding: var(--oas-space-2) var(--oas-space-3);
  font-size: var(--oas-font-size-sm);
  color: var(--oas-color-text-primary);
}
.dropdown-header {
  border-bottom: 1px solid var(--oas-color-border);
  margin-bottom: var(--oas-space-1);
}
.dropdown-footer {
  border-top: 1px solid var(--oas-color-border);
  margin-top: var(--oas-space-1);
}
.listbox {
  max-height: var(--oas-select-dropdown-height, 240px);
  overflow-y: auto;
}
.option-group {
  padding: var(--oas-space-2) var(--oas-space-3) var(--oas-space-1);
  font-size: var(--oas-font-size-sm);
  color: var(--oas-color-text-secondary);
  user-select: none;
  cursor: default;
}
.option-group + .option-group {
  margin-top: var(--oas-space-2);
}
${OPTION_STYLE}
.create-option {
  color: var(--oas-color-primary);
}
.empty {
  padding: var(--oas-space-3);
  text-align: center;
  color: var(--oas-color-text-secondary);
  font-size: var(--oas-font-size-sm);
}
/* 触摸目标抬升：coarse pointer 下 option 行最小高度 ≥44px（token 开口可调）。
   只进 select 自身 shadow——虚拟模式行高由 vlist 布局数学固定（item-height），
   不在 vlist 注入，避免行高与定位数学打架 */
${TOUCH_TARGET_CSS}
`

export class OASSelect extends OASFormElement {
  /** 原生表单集成（label for / FormData / reset / fieldset disabled）；沿静态原型链已可继承，显式声明便于阅读与检索 */
  static override formAssociated = true

  static override get observedAttributes(): string[] {
    return [
      'value',
      'placeholder',
      'options',
      'disabled',
      'multiple',
      'searchable',
      'clearable',
      'remote',
      'loading',
      'max-tag-count',
      'allow-create',
      'virtual',
      // 能力缺口 D20：多选时已选选项从下拉列表隐藏（清空/取消后回到列表）
      'hide-selected',
      'item-height',
      'disabled-skip',
      'size',
      'status',
      'variant',
      'label',
      'open',
      'max-count',
      'placement',
      'readonly',
      'debounce',
      // 能力缺口 P2 长尾组：搜索输入受控/箭头开关/自动聚焦/首项高亮/加载文案/保留关键词/tabindex 透传/自适应宽/提示文案
      'input-value',
      'show-arrow',
      'autofocus',
      'default-active-first-option',
      'loading-text',
      'reserve-keyword',
      'tabindex',
      'auto-width',
      'hint',
      // 触发器预留最宽选项宽（opt-in）：切换选中值时触发器尺寸不抖动
      'reserve-width',
      // 表单关联通道：required 驱动原生校验链（valueMissing）；name 变化需重同步多选 FormData 的 entry key
      'name',
      'required',
    ]
  }

  private triggerEl: HTMLButtonElement | null = null
  private dropdown: HTMLElement | null = null
  private listbox: HTMLElement | null = null
  private vlist: OASVirtualList | null = null
  /** 移动端底部抽屉承载件（oas-bottom-sheet；PC 形态 passive 透传） */
  private sheetEl: OASBottomSheet | null = null
  private _options: Option[] = []
  /** 子元素通道观察器：light DOM 里 oas-option 增删或属性/文本变化 → 重解析渲染 */
  private childObserver: MutationObserver | null = null

  /** Vue/React 会把 options 识别为实例属性走 property 赋值；setter 反射到 attribute 统一解析链路 */
  get options(): Option[] {
    return this._options
  }
  set options(value: Option[] | string) {
    this.setAttribute('options', typeof value === 'string' ? value : JSON.stringify(value))
  }

  /**
   * 自定义本地过滤函数（JS property 通道，attribute 传不了函数）：
   * `(query, option) => boolean`，query 为原始输入（trim 后未转小写），option 为完整选项对象。
   * 仅本地过滤模式生效（remote 模式数据面过滤由宿主负责）；置 null 回落默认 label includes。
   */
  filterMethod: ((query: string, option: Option) => boolean) | null = null

  /** 受控展开（property 通道反射 attribute，宿主框架 :open 绑定可达）；getter 返回当前生效展开态 */
  get open(): boolean {
    return this.openState
  }
  set open(value: boolean) {
    if (value) {
      this.setAttribute('open', '')
    } else {
      this.removeAttribute('open')
      // 属性不在场时 removeAttribute 不触发 attributeChangedCallback，非受控展开态需直接收起
      if (this.openState) {
        this.openState = false
        this.syncDropdown()
      }
    }
  }

  private activeIndex = 0
  private openState = false
  /** allow-create 时无匹配展示的「创建 xxx」行状态 */
  private createVisible = false
  private createLabel = ''
  /** allow-create 已创建选项持久层（源数据每轮重解析，创建项不随源刷新丢失） */
  private createdOptions: Option[] = []
  /** 焦点在组件内（trigger/搜索框/chip 按钮任一）：内部转移不派发 oas-focus/oas-blur */
  private focusWithin = false
  /** remote 防抖派发计时器 */
  private inputTimer: number | null = null
  /** aria-invalid 由 status=error 设置的所有权标志（清理时只移除自己设置的，不动宿主自设值） */
  private invalidByStatus = false
  /**
   * @apiProperty 当前值：单选为选中值字符串（无选中回落空串）；`multiple` 时为选中值数组
   * （等价既有 currentValues()，与 oas-change detail 一致）。
   */
  get value(): string | string[] {
    const values = this.currentValues()
    return this.hasAttr('multiple') ? values : (values[0] ?? '')
  }

  /**
   * 程序性写值（受控赋值即生效语义）：写受控 `value` 属性（多选 JSON 数组）并强制 update
   * 重建列表选中态与触发器回显，不派发任何事件。同值 `setAttribute` 不触发
   * attributeChangedCallback，显式 update 保证生效。
   */
  set value(v: string | string[]) {
    if (this.hasAttr('multiple')) {
      const arr = Array.isArray(v) ? v.map((x) => String(x)) : v == null || v === '' ? [] : [String(v)]
      this.setAttribute('value', JSON.stringify(arr))
    } else {
      const s = Array.isArray(v) ? (v[0] ?? '') : v == null ? '' : String(v)
      this.setAttribute('value', s)
    }
    if (this.hasRendered) this.update()
  }

  /** 初始选中基线（form.reset 恢复目标）：初始渲染/受控写入跟随 value 属性刷新（多选为数组快照） */
  private initialValue: string[] = []
  /** 用户交互脏标记（对齐原生 dirty 语义）：置位后基线冻结，reset 恢复基线并清脏 */
  private valueDirty = false

  /**
   * 受控 open：属性即真相——在场=展开、移除=收起（宿主手势只派发
   * oas-open-change 通知宿主，由宿主决定是否增删属性，组件不强制写回）。
   */
  override attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null): void {
    if (name === 'open' && oldValue !== newValue) {
      this.openState = newValue !== null
    }
    super.attributeChangedCallback(name, oldValue, newValue)
  }

  /** 纯函数：SSR 快照与客户端渲染共用同一份模板，保证两路径结构严格一致 */
  private template(): string {
    return `
      <style>${STYLE}</style>
      <div class="wrapper" part="wrapper">
        <button class="trigger" part="trigger" type="button" role="combobox"
          aria-haspopup="listbox" aria-expanded="false">
          <span class="prefix" part="prefix" hidden></span>
          <span class="value" part="value"></span>
          <span class="clear-btn" part="clear" role="button" tabindex="-1" hidden aria-label="">
            <svg class="clear-default" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
              <path d="M4 4 L12 12 M12 4 L4 12" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
            </svg>
            <span class="clear-icon" part="clear-icon" hidden></span>
          </span>
          <span class="suffix" part="suffix" hidden></span>
          <span class="suffix-icon" part="suffix-icon" hidden></span>
          <svg class="chevron" width="12" height="12" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
            <path d="M4 6 L8 10 L12 6" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>
        <div class="hint" part="hint" id="oas-select-hint" hidden></div>
        <oas-bottom-sheet part="sheet" passive>
          <div class="dropdown" part="dropdown">
            <input class="search-input" part="search-input" type="text" hidden />
            <div class="dropdown-header" part="header" hidden></div>
            <div class="listbox" part="listbox" role="listbox"></div>
            <oas-virtual-list class="vlist" part="virtual-list" hidden></oas-virtual-list>
            <div class="dropdown-footer" part="footer" hidden></div>
          </div>
        </oas-bottom-sheet>
      </div>
    `
  }

  /** 缓存节点引用 + 绑定触发器/搜索/清空/虚拟列表事件 + 注册清理（render 与水合路径共用） */
  private bind(): void {
    this.triggerEl = this.shadow.querySelector('.trigger')
    this.dropdown = this.shadow.querySelector('.dropdown')
    this.listbox = this.shadow.querySelector('.listbox')
    this.vlist = this.shadow.querySelector<OASVirtualList>('oas-virtual-list')
    // 移动端底部抽屉承载件：oas-close（下滑/backdrop/Esc）→ 同步收起
    this.sheetEl = this.shadow.querySelector<OASBottomSheet>('oas-bottom-sheet')
    this.sheetEl?.addEventListener('oas-close', () => this.requestOpen(false))

    this.shadow.querySelector<HTMLInputElement>('.search-input')?.addEventListener('input', (e) => {
      const v = (e.target as HTMLInputElement).value
      const searchInput = this.shadow.querySelector<HTMLInputElement>('.search-input')
      if (searchInput) searchInput.setAttribute('data-query', v)
      // remote 模式下过滤交给宿主：组件不本地过滤，只派发 oas-input 供宿主请求；
      // debounce > 0 时按窗口合并派发（默认 0 立即，不干扰现状；本地过滤不受影响）
      if (this.hasAttr('remote')) {
        if (this.inputTimer !== null) window.clearTimeout(this.inputTimer)
        const delay = this.inputDebounce()
        if (delay > 0) {
          this.inputTimer = window.setTimeout(() => {
            this.inputTimer = null
            this.emit('input', { value: v })
          }, delay)
        } else {
          this.emit('input', { value: v })
        }
      }
      // input-value 受控镜像：用户输入写回属性（DOM 为真相，宿主可监听/读取），
      // 属性变化经 update 重渲染；值未变化时这里兜底刷一次列表
      if (this.getAttribute('input-value') !== v) this.setAttribute('input-value', v)
      else this.renderListbox()
      this.emit('input-value-change', { value: v })
    })
    this.shadow
      .querySelector<HTMLInputElement>('.search-input')
      ?.addEventListener('keydown', (e: KeyboardEvent) => this.handleSearchKey(e))
    this.shadow.querySelector<HTMLButtonElement>('.clear-btn')?.addEventListener('click', (e: MouseEvent) => {
      e.stopPropagation()
      this.clearValue()
    })

    this.triggerEl?.addEventListener('click', () => this.toggle())
    this.triggerEl?.addEventListener('keydown', (e: KeyboardEvent) => this.handleTriggerKey(e))
    // autofocus：原生 autofocus 不穿透 shadow，挂载后手动聚焦 trigger 一次（对齐 oas-input 转接 pattern）
    if (this.hasAttr('autofocus')) {
      queueMicrotask(() => this.triggerEl?.focus())
    }
    // 焦点事件：组件整体获得/失去焦点派发 oas-focus / oas-blur
    //（trigger↔搜索框↔chip 按钮的组件内转移不误报，离开组件才 blur）
    const wrapper = this.shadow.querySelector<HTMLElement>('.wrapper')
    wrapper?.addEventListener('focusin', () => {
      if (this.focusWithin) return
      this.focusWithin = true
      this.emit('focus')
    })
    wrapper?.addEventListener('focusout', (e: FocusEvent) => {
      if (!this.focusWithin) return
      const related = e.relatedTarget
      if (related instanceof Node && wrapper.contains(related)) return
      this.focusWithin = false
      this.emit('blur')
    })
    // 虚拟滚动：复用 oas-virtual-list 的窗口计算，把每个可见项渲染为选项行
    this.vlist?.addEventListener('oas-item', ((
      e: CustomEvent<{ index: number; item: Option; element: HTMLElement }>,
    ) => {
      const detail = e.detail
      if (detail && detail.item && detail.element) {
        this.createOptionRow(detail.item, detail.index, this.currentValues(), detail.element)
      }
    }) as EventListener)
    this.onCleanup(() => document.removeEventListener('click', this.handleOutsideClick, true))
    this.onCleanup(() => {
      if (this.inputTimer !== null) window.clearTimeout(this.inputTimer)
    })
    // 视口/指针形态变化（缩放/横竖屏/设备仿真）时重判定移动/PC 形态（不强刷）
    this.onCleanup(watchMobileSheetMode(() => this.resyncMobileMode()))
  }

  protected override render(): void {
    this.shadow.innerHTML = this.template()
    this.bind()
    this.update()
  }

  /** 真水合：校验 SSR 快照结构（trigger/dropdown/listbox 存在）后直接接管，跳过 shadow 重建 */
  protected override hydrate(): boolean {
    if (!this.shadow.querySelector('.trigger')) return false
    if (!this.shadow.querySelector('.dropdown')) return false
    if (!this.shadow.querySelector('.listbox')) return false
    this.bind()
    return true
  }

  protected override update(): void {
    // 移动形态同步：coarse pointer（触屏）或窄视口（<768px）→ bottom-sheet 底部抽屉承载
    this.syncMobileMode()
    // 子元素通道观察器（重连后重建；options 属性显式时子元素被忽略，观察器空转无副作用）
    this.ensureChildObserver()
    // tabindex 委托（宿主属性转移到内部 trigger）
    this.syncTabindex()
    this.parseOptions()
    // form.reset 基线：初始渲染/受控写入（非用户交互的属性变化）跟随 value 属性刷新；
    // 用户交互置脏后基线冻结（与原生 dirty checkedness 同思路）
    if (!this.valueDirty) this.initialValue = this.currentValues()
    // size/status 镜像（size 就近读取 config-provider 注入，与全局密度联动）
    this.syncSizeStatus()
    // 下拉头尾插槽（header/footer template 克隆）
    this.syncDropdownChrome()
    // input-value 受控同步（先于列表渲染，预设查询词参与过滤）
    this.syncInputValue()
    // 内置文案走 locale registry（zh-CN 默认，setLocale 切换自动刷新）
    this.shadow.querySelector<HTMLInputElement>('.search-input')?.setAttribute('aria-label', this.t('select.search'))
    this.renderListbox()
    this.syncTrigger()
    // 触发器前后缀/自定义图标 + 下方提示文案
    this.syncTriggerAffixes()
    this.syncHint()
    // reserve-width：触发器预留最宽选项宽（options/子元素变化随 update 自动重测）
    this.syncReserveWidth()
    // 原生表单数据 + 校验链同步（form-associated；无 name 时浏览器自动不提交）
    this.syncFormValue()
    this.syncValidity()
    // 展开态同步（初始 open 属性、展开中的属性变化重定位等）
    this.syncDropdown()
  }

  /**
   * tabindex 委托：宿主 tabindex 转移到内部 trigger（组合控件无原生 tabindex 通道）。
   * 宿主属性写完即移除——避免宿主 + trigger 双 Tab 停靠点；宿主再设任意值（含同值）
   * 会重新委托（幂等收敛）；非法整数值视为不参与 Tab 序（移除 trigger tabindex）。
   */
  private delegatedTabindex: string | null = null
  private syncTabindex(): void {
    const raw = this.getAttribute('tabindex')
    if (raw !== null) {
      this.delegatedTabindex = raw
      this.removeAttribute('tabindex')
      if (this.triggerEl) {
        const n = Number.parseInt(raw, 10)
        if (Number.isNaN(n)) this.triggerEl.removeAttribute('tabindex')
        else this.triggerEl.tabIndex = n
      }
    }
  }

  /**
   * input-value 受控同步（searchable 搜索框）：属性在场即为真相源——预设/外部更新
   * 同步进搜索框（含 data-query 查询镜像）并参与过滤；属性缺席走非受控内部状态。
   */
  private syncInputValue(): void {
    if (!this.hasAttribute('input-value')) return
    const v = this.getAttr('input-value', '')
    const input = this.shadow.querySelector<HTMLInputElement>('.search-input')
    if (!input) return
    if (input.value !== v) input.value = v
    if (input.getAttribute('data-query') !== v) input.setAttribute('data-query', v)
  }

  /** show-arrow：默认显示下拉箭头；显式 "false" 关闭（suffix-icon 模板在场时优先替换） */
  private showArrow(): boolean {
    return this.getAttr('show-arrow', 'true') !== 'false'
  }

  /**
   * 触发器前后缀插槽与图标替换：
   * - template[slot="prefix"/"suffix"] 克隆进触发器前后缀容器（缺省隐藏）
   * - template[slot="suffix-icon"] 替换默认 chevron（展开旋转样式同容器生效）
   * - template[slot="clear-icon"] 替换清空按钮默认 × 图标
   * 注意：svg 元素无 hidden IDL 属性（SVGElement 无该通道），显隐一律走 attribute。
   */
  private syncTriggerAffixes(): void {
    const trigger = this.triggerEl
    if (!trigger) return
    this.fillAffix('.prefix', 'prefix')
    this.fillAffix('.suffix', 'suffix')
    const iconBox = trigger.querySelector<HTMLElement>('.suffix-icon')
    const chevron = trigger.querySelector('.chevron')
    if (iconBox && chevron) {
      const tpl = this.querySelector('template[slot="suffix-icon"]')
      if (tpl instanceof HTMLTemplateElement) {
        iconBox.innerHTML = ''
        iconBox.appendChild(tpl.content.cloneNode(true))
        iconBox.hidden = false
        chevron.setAttribute('hidden', '')
      } else {
        iconBox.innerHTML = ''
        iconBox.hidden = true
        chevron.toggleAttribute('hidden', !this.showArrow())
      }
    }
    const clearBtn = this.shadow.querySelector<HTMLElement>('.clear-btn')
    const clearSvg = clearBtn?.querySelector('svg')
    const clearBox = clearBtn?.querySelector<HTMLElement>('.clear-icon')
    if (clearBtn && clearSvg && clearBox) {
      const tpl = this.querySelector('template[slot="clear-icon"]')
      if (tpl instanceof HTMLTemplateElement) {
        clearBox.innerHTML = ''
        clearBox.appendChild(tpl.content.cloneNode(true))
        clearBox.hidden = false
        clearSvg.setAttribute('hidden', '')
      } else {
        clearBox.innerHTML = ''
        clearBox.hidden = true
        clearSvg.removeAttribute('hidden')
      }
    }
  }

  /** 前后缀插槽容器：有 template[slot] 则克隆填充并显示，否则清空隐藏 */
  private fillAffix(selector: string, slot: string): void {
    const el = this.triggerEl?.querySelector<HTMLElement>(selector)
    if (!el) return
    const tpl = this.querySelector(`template[slot="${slot}"]`)
    if (tpl instanceof HTMLTemplateElement) {
      el.innerHTML = ''
      el.appendChild(tpl.content.cloneNode(true))
      el.hidden = false
    } else {
      el.innerHTML = ''
      el.hidden = true
    }
  }

  /** hint：触发器下方提示文案（aria-describedby 关联，读屏可达），空值隐藏 */
  private syncHint(): void {
    const hint = this.shadow.querySelector<HTMLElement>('.hint')
    if (!hint) return
    const text = this.getAttr('hint', '')
    hint.textContent = text
    hint.hidden = text === ''
    if (!this.triggerEl) return
    if (text) this.triggerEl.setAttribute('aria-describedby', 'oas-select-hint')
    else this.triggerEl.removeAttribute('aria-describedby')
  }

  // ===== reserve-width：触发器预留最宽选项（切值不抖） =====

  /**
   * 测最宽选项 label + placeholder 文本宽，经 `--oas-select-reserve` 变量锁宿主
   * min-width（opt-in `reserve-width`；与 auto-width 组合最典型——宽度随内容收缩后
   * 由预留值托底，切换选中值不抖）。文本量测走 shared 三级降级（canvas → 离屏 span
   * → 估算），options/子元素变化经 update 自动重测。文本宽为近似值（触发器 chrome
   * 余量经 --oas-select-reserve-pad 开口调整），目标是「切换不抖」而非像素级排版。
   */
  private syncReserveWidth(): void {
    if (!this.hasAttr('reserve-width')) {
      if (this.style.getPropertyValue('--oas-select-reserve') !== '') {
        this.style.removeProperty('--oas-select-reserve')
      }
      return
    }
    const texts = this._options.map((o) => o.label)
    const placeholder = this.getAttr('placeholder', this.t('select.placeholder'))
    if (placeholder) texts.push(placeholder)
    const cs = getComputedStyle(this)
    const w = measureMaxTextWidth(texts, { fontSize: cs.fontSize, fontFamily: cs.fontFamily })
    if (w <= 0) {
      // 无可计量测文本（无选项且无 placeholder）：无预留，写 0 不占宿主盒
      this.style.setProperty('--oas-select-reserve', '0px')
      return
    }
    const pad = cssVarPx(this, '--oas-select-reserve-pad', 46)
    this.style.setProperty('--oas-select-reserve', `${Math.ceil(w + pad)}px`)
  }

  /** 移动形态判定：触屏（coarse pointer）或窄视口（<768px）→ 下拉由 bottom-sheet 底部抽屉承载 */
  private isMobileSheet(): boolean {
    if (typeof window === 'undefined') return false
    if (typeof window.matchMedia === 'function' && window.matchMedia('(pointer: coarse)').matches) return true
    return window.innerWidth < 768
  }

  /** 移动形态同步：移动端 bottom-sheet 去 passive 变容器、宿主打 data-mobile-sheet（dropdown 静态化）；PC 恢复 passive */
  private syncMobileMode(): void {
    const mobile = this.isMobileSheet()
    this.toggleAttribute('data-mobile-sheet', mobile)
    this.sheetEl?.toggleAttribute('passive', !mobile)
  }

  /** 移动/PC 形态切换时重同步：重判定形态 + 展开态重排承载方式（syncDropdown 的 PC 分支自带 positionDropdown） */
  private resyncMobileMode(): void {
    this.syncMobileMode()
    this.syncDropdown()
  }

  private toggle(): void {
    if (this.injectDisabled() || this.hasAttr('readonly')) return
    this.requestOpen(!this.openState)
  }

  /**
   * 展开/收起请求（trigger 点击/键盘/外部点击/选中收起的统一入口）。
   * 非受控：直接应用并派发 oas-open-change；受控（open 属性在场）：只派发事件不强制写回——
   * 视觉由 open 属性决定，宿主监听事件决定是否增删属性。
   */
  private requestOpen(next: boolean): void {
    if (this.hasAttribute('open')) {
      this.emit('open-change', { open: next })
      return
    }
    this.openState = next
    this.syncDropdown()
    this.emit('open-change', { open: next })
  }

  private syncDropdown(): void {
    if (!this.dropdown || !this.triggerEl) return
    const wasOpen = this.dropdown.classList.contains('open')
    this.dropdown.classList.toggle('open', this.openState)
    this.triggerEl.setAttribute('aria-expanded', String(this.openState))
    const searchInput = this.shadow.querySelector<HTMLInputElement>('.search-input')
    if (searchInput) {
      searchInput.hidden = !this.hasAttr('searchable')
      // 仅「收起→展开」瞬间聚焦搜索框；展开中的属性变化重同步不抢焦点
      if (this.openState && !wasOpen && this.hasAttr('searchable')) {
        searchInput.focus()
      }
    }
    const mobile = this.isMobileSheet()
    if (this.openState) {
      document.addEventListener('click', this.handleOutsideClick, true)
      // 移动形态：bottom-sheet 承载容器（设置 open 展开底部抽屉）；PC 形态：computePosition 锚定 trigger
      if (mobile) {
        this.sheetEl?.setAttribute('open', '')
      } else {
        this.sheetEl?.removeAttribute('open')
        this.positionDropdown()
      }
      // 展开时重读下拉高度变量（宿主改 --oas-select-dropdown-height 后无需触发属性变化，重开即生效）
      this.vlist?.setAttribute('height', String(this.dropdownHeight()))
      if (!wasOpen) {
        // default-active-first-option：展开高亮首个可见项（缺省高亮当前选中项）
        if (this.hasAttr('default-active-first-option')) {
          this.activeIndex = 0
        } else {
          const current = this.currentValues()
          const idx = current.length > 0 ? this.visibleOptions().findIndex((o) => o.value === current[0]) : 0
          this.activeIndex = Math.max(idx, 0)
        }
        this.scrollActiveIntoView()
        this.syncActive()
      }
    } else {
      this.sheetEl?.removeAttribute('open')
      document.removeEventListener('click', this.handleOutsideClick, true)
      this.syncAriaActiveDescendant()
    }
  }

  private handleOutsideClick = (e: MouseEvent): void => {
    const path = e.composedPath()
    if (!path.includes(this) && !path.some((n) => n instanceof Node && this.shadow.contains(n))) {
      this.requestOpen(false)
    }
  }

  /**
   * fixed 定位：宽度对齐 trigger（同 combobox），左缘对齐 trigger 左缘（bottom-start/top-start）。
   * placement：auto（默认）= 下方优先 + 空间不足自动翻转（现状行为）；
   * top / bottom = 强制方向不翻转不避让（宿主显式指定时尊重声明，可能溢出视口）。
   */
  private positionDropdown(): void {
    if (!this.dropdown || !this.triggerEl) return
    const anchorRect = this.triggerEl.getBoundingClientRect()
    // 先按 trigger 宽度撑开面板，再测量/定位：dropdown 是 auto 宽度（固有宽度=选项文字宽，远小于
    // trigger），若在撑开前测量，对齐会按塌缩宽度算 left → 首次展开偏右；再次展开因内联宽度仍在才对齐。
    this.dropdown.style.width = `${anchorRect.width}px`
    const panelRect = this.dropdown.getBoundingClientRect()
    const raw = this.getAttr('placement', 'auto')
    const forced = raw === 'top' || raw === 'bottom'
    const { top, left } = computePosition(
      anchorRect,
      panelRect,
      (forced ? `${raw}-start` : 'bottom-start') as Placement,
      getViewport(),
      8,
      !forced,
      { direction: resolveDirection(this) },
    )
    this.dropdown.style.top = `${top}px`
    this.dropdown.style.left = `${left}px`
  }

  /** trigger 键盘：Esc 关闭；关闭态 Enter/Space/↑/↓ 展开（readonly 拦截）；展开态 ↑/↓ 移动、Enter 选中 */
  private handleTriggerKey(e: KeyboardEvent): void {
    if (this.injectDisabled()) return
    // 焦点在 trigger 内嵌按钮（清空/移除 chip）时不响应，交给按钮原生行为
    if ((e.target as Element).closest('.clear-btn, .chip button')) return
    if (e.key === 'Escape') {
      this.requestOpen(false)
    } else if (!this.openState) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault()
        if (this.hasAttr('readonly')) return
        this.requestOpen(true)
      }
    } else {
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        this.moveActive(1)
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        this.moveActive(-1)
      } else if (e.key === 'Enter') {
        e.preventDefault()
        this.selectActive()
      }
    }
  }

  /** 搜索框键盘：↑/↓ 移动、Enter 选中、Esc 关闭并还焦 trigger */
  private handleSearchKey(e: KeyboardEvent): void {
    if (e.key === 'Escape') {
      e.preventDefault()
      this.requestOpen(false)
      this.triggerEl?.focus()
    } else if (e.key === 'ArrowDown') {
      e.preventDefault()
      this.moveActive(1)
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      this.moveActive(-1)
    } else if (e.key === 'Enter') {
      e.preventDefault()
      this.selectActive()
    }
  }

  /** 可导航行数：可见选项 +（allow-create 无匹配时的「创建」行） */
  private navigableCount(): number {
    return this.visibleOptions().length + (this.createVisible ? 1 : 0)
  }

  /**
   * 移动高亮：增量同步（不重建 DOM）——虚拟滚动下重建会打断窗口渲染，
   * 改走 scrollActiveIntoView + syncActive（class / aria-activedescendant / 选中态）
   */
  private moveActive(dir: 1 | -1): void {
    const n = this.navigableCount()
    if (n === 0) return
    this.activeIndex = (this.activeIndex + dir + n) % n
    this.scrollActiveIntoView()
    this.syncActive()
  }

  private selectActive(): void {
    const visible = this.visibleOptions()
    if (this.createVisible && this.activeIndex >= visible.length) {
      if (this.blockedByLimit(this.createLabel)) return
      this.createOption()
      return
    }
    const option = visible[this.activeIndex]
    if (!option || option.disabled) return
    if (this.blockedByLimit(option.value)) return
    this.selectValue(option.value)
  }

  /**
   * 当前下拉可见选项：
   * - remote 模式：不做本地过滤（过滤交给宿主，直接渲染 options）
   * - 本地模式：有查询词时按 label 过滤（filterMethod 设置时走自定义函数）
   * - hide-selected（仅多选）：已选选项从下拉隐藏（值集变化 → 重渲染自动回到列表）
   */
  private visibleOptions(): Option[] {
    if (this.hasAttr('remote')) return this._options
    const q = this.currentQuery()
    let list: Option[]
    if (q === '') {
      list = this._options
    } else if (typeof this.filterMethod === 'function') {
      // 自定义过滤函数（JS property 通道）：收到原始查询词与完整 option 对象
      list = this._options.filter((o) => this.filterMethod?.(q, o) === true)
    } else {
      const lower = q.toLowerCase()
      list = this._options.filter((o) => o.label.toLowerCase().includes(lower))
    }
    if (this.hasAttr('hide-selected') && this.hasAttr('multiple')) {
      const selected = this.currentValues()
      if (selected.length > 0) list = list.filter((o) => !selected.includes(o.value))
    }
    return list
  }

  /** 搜索框原始查询词（trim 后，保留原始大小写供「创建」用） */
  private currentQuery(): string {
    return (this.shadow.querySelector<HTMLInputElement>('.search-input')?.getAttribute('data-query') ?? '').trim()
  }

  /** 虚拟滚动定高：默认 36（与 oas-virtual-list 默认一致，匹配选项行视觉高度） */
  private virtualItemHeight(): number {
    const raw = this.getAttr('item-height', '36')
    const n = Number.parseInt(raw, 10)
    return Number.isNaN(n) ? 36 : n
  }

  private renderListbox(): void {
    const listbox = this.listbox
    if (!listbox) return
    listbox.innerHTML = ''
    this.createVisible = false

    // loading 占位态：remote 模式下宿主请求期间显示（文案 loading-text 优先，缺省走 locale）
    if (this.hasAttr('loading')) {
      this.setVirtualVisible(false)
      const loading = document.createElement('div')
      loading.className = 'empty'
      loading.textContent = this.getAttr('loading-text', '') || this.t('loading.loading')
      listbox.appendChild(loading)
      this.syncActive()
      return
    }

    const visible = this.visibleOptions()
    const query = this.currentQuery()

    if (visible.length === 0) {
      this.setVirtualVisible(false)
      // allow-create：无匹配时展示「创建 xxx」行（选中后以输入值新建选项）
      if (this.hasAttr('allow-create') && query !== '') {
        this.createVisible = true
        this.createLabel = query
        const row = document.createElement('div')
        row.className = 'option create-option'
        row.setAttribute('role', 'option')
        row.setAttribute('aria-selected', 'false')
        row.id = `opt-${visible.length}` // aria-activedescendant 指向创建行
        if (this.activeIndex === visible.length) row.classList.add('active')
        const label = document.createElement('span')
        label.textContent = this.t('select.create', { label: query })
        row.append(label)
        row.addEventListener('click', () => this.createOption())
        row.addEventListener('mousemove', () => {
          this.activeIndex = visible.length
          this.syncActive()
        })
        listbox.appendChild(row)
      } else {
        const empty = document.createElement('div')
        empty.className = 'empty'
        this.fillEmpty(empty, query ? this.t('select.noMatch') : this.t('select.empty'))
        listbox.appendChild(empty)
      }
      this.syncActive()
      return
    }

    // 虚拟滚动：大数据量时复用 oas-virtual-list 仅渲染可见窗口；
    // 带 group 的选项回退非虚拟全量渲染（组标题是不同行高的流式分隔，虚拟定高模型不适配）；
    // vlist 缺失（如手写 DSD 快照无此元素）时同样回退全量渲染，避免静默空下拉
    if (this.hasAttr('virtual') && this.vlist && !visible.some((o) => o.group !== undefined)) {
      this.renderVirtualList(visible)
      this.syncActive()
      return
    }

    this.setVirtualVisible(false)
    const values = this.currentValues()
    let prevGroup: string | undefined
    let optionIdx = 0
    for (const option of visible) {
      // 分组标题：组字段变化时插入（不可选，仅展示）
      if (option.group !== undefined && option.group !== prevGroup) {
        const groupEl = document.createElement('div')
        groupEl.className = 'option-group'
        groupEl.textContent = option.group
        listbox.appendChild(groupEl)
      }
      prevGroup = option.group
      this.createOptionRow(option, optionIdx, values, listbox)
      optionIdx++
    }
    this.syncActive()
  }

  /** 构建一个选项行（角色/aria/高亮/点击/自定义渲染），非虚拟与虚拟（vlist oas-item）两路共用 */
  private createOptionRow(option: Option, optionIdx: number, values: string[], container: HTMLElement): void {
    const row = document.createElement('div')
    row.className = 'option'
    if (option.group !== undefined) row.classList.add('grouped')
    row.setAttribute('part', 'option')
    row.setAttribute('role', 'option')
    row.setAttribute('aria-selected', String(values.includes(option.value)))
    // max-count 达上限：未选项禁用置灰（已选项仍可取消），与选项自身 disabled 同视觉
    const disabledByLimit = !values.includes(option.value) && this.limitReached()
    row.setAttribute('aria-disabled', String(option.disabled === true || disabledByLimit))
    row.id = `opt-${optionIdx}` // aria-activedescendant 锚点（shadow 内 id 作用域隔离，无宿主冲突）
    row.setAttribute('data-index', String(optionIdx))
    if (optionIdx === this.activeIndex) row.classList.add('active')
    const label = document.createElement('span')
    label.className = 'option-label'
    this.fillOptionLabel(label, option)
    const check = document.createElement('span')
    check.className = 'check'
    check.textContent = '✓'
    row.append(label, check)
    row.addEventListener('click', () => {
      if (option.disabled) return
      if (this.blockedByLimit(option.value)) return
      this.selectValue(option.value)
    })
    row.addEventListener('mousemove', () => {
      if (this.activeIndex === optionIdx) return
      this.activeIndex = optionIdx
      this.syncActive()
    })
    container.appendChild(row)
    // 自定义选项渲染：宿主可监听改写 element（图标/富文本），机制与 virtual-list 的 oas-item 一致
    this.emit('option-render', { index: optionIdx, option, element: label })
  }

  /** 空态渲染：template[slot="empty"] 克隆覆盖默认空态/无匹配文案（两态共用） */
  private fillEmpty(emptyEl: HTMLElement, fallbackText: string): void {
    const tpl = this.querySelector('template[slot="empty"]')
    if (tpl instanceof HTMLTemplateElement) {
      emptyEl.appendChild(tpl.content.cloneNode(true))
    } else {
      emptyEl.textContent = fallbackText
    }
  }

  /**
   * size/status 镜像到宿主 data-*（供 :host([data-*]) 样式消费）。
   * size 就近读取 config-provider 注入（与全局密度联动）；status=error 联动 aria-invalid——
   * 所有权标志保证清理时只移除 status 设置的，不动宿主自设的 aria-invalid。
   */
  private syncSizeStatus(): void {
    const size = normalizeChoice(this.injectValue('size', 'medium'), 'medium', VALID_SIZES)
    this.setAttribute('data-size', size)
    // variant 形态镜像（非法值静默回落 outlined，对齐 oas-input）
    const variant = normalizeChoice(this.getAttr('variant', ''), 'outlined', VALID_VARIANTS)
    this.setAttribute('data-variant', variant)
    const status = normalizeChoice(this.getAttr('status', ''), '', VALID_STATUSES)
    if (status) this.setAttribute('data-status', status)
    else this.removeAttribute('data-status')
    if (status === 'error') {
      if (!this.hasAttribute('aria-invalid')) this.invalidByStatus = true
      this.setAttribute('aria-invalid', 'true')
    } else if (this.invalidByStatus) {
      this.invalidByStatus = false
      this.removeAttribute('aria-invalid')
    }
  }

  /**
   * 下拉头尾插槽：template[slot="header"] / template[slot="footer"] 克隆进下拉头尾
   * （header 在搜索框之下、选项之上；footer 在选项之下）。
   * 注意：键盘 ↑/↓ 导航不进入头尾区——交互内容建议建模为选项（如「全选」选项）或由宿主自管焦点。
   */
  private syncDropdownChrome(): void {
    this.syncSlotChrome('header', '.dropdown-header')
    this.syncSlotChrome('footer', '.dropdown-footer')
  }

  private syncSlotChrome(slot: string, selector: string): void {
    const container = this.shadow.querySelector<HTMLElement>(selector)
    if (!container) return
    const tpl = this.querySelector(`template[slot="${slot}"]`)
    if (tpl instanceof HTMLTemplateElement) {
      container.innerHTML = ''
      container.appendChild(tpl.content.cloneNode(true))
      container.hidden = false
    } else {
      container.innerHTML = ''
      container.hidden = true
    }
  }

  /** 选项 label 渲染：template[slot="option"] 克隆 + [data-option-label] 绑定，缺省回落纯文本 */
  private fillOptionLabel(labelEl: HTMLElement, option: Option): void {
    const tpl = this.querySelector('template[slot="option"]')
    if (tpl instanceof HTMLTemplateElement) {
      labelEl.appendChild(tpl.content.cloneNode(true))
      const binder = labelEl.querySelector('[data-option-label]')
      if (binder) binder.textContent = option.label
    } else {
      labelEl.textContent = option.label
    }
  }

  /** 虚拟模式：切到 vlist 渲染（保证行样式/视口键盘可达性）并喂入可见选项 */
  private renderVirtualList(visible: Option[]): void {
    const vlist = this.vlist
    if (!vlist) return
    this.setVirtualVisible(true)
    // 行样式注入 vlist shadow（虚拟行在 vlist shadow 内，select 自身样式够不到）
    const vlistRoot = vlist.shadowRoot
    if (vlistRoot && !vlistRoot.querySelector('style[data-oas-select-rows]')) {
      const style = document.createElement('style')
      style.setAttribute('data-oas-select-rows', '')
      style.textContent = VIRTUAL_ROW_STYLE
      vlistRoot.appendChild(style)
    }
    // 视口键盘可达性由 trigger 的 combobox 键盘流负责，去掉 vlist 内层 tabindex 避免多余 Tab 停靠点
    vlistRoot?.querySelector<HTMLElement>('.viewport')?.removeAttribute('tabindex')
    vlist.setAttribute('items-role', 'listbox')
    vlist.setAttribute('item-role', 'presentation')
    vlist.setAttribute('height', String(this.dropdownHeight()))
    vlist.setAttribute('item-height', String(this.virtualItemHeight()))
    vlist.items = visible
  }

  /** 下拉高度：--oas-select-dropdown-height CSS 变量开口（默认 240px，宿主可覆盖；支持 "300px"/"300" 写法）。
   *  computed 优先（覆盖继承/类样式等场景），inline style 兜底（宿主直设 host 内联的主通道） */
  private dropdownHeight(): number {
    const raw =
      getComputedStyle(this).getPropertyValue('--oas-select-dropdown-height').trim() ||
      this.style.getPropertyValue('--oas-select-dropdown-height').trim()
    const n = Number.parseInt(raw, 10)
    return Number.isNaN(n) || n <= 0 ? 240 : n
  }

  private setVirtualVisible(visible: boolean): void {
    if (this.listbox) this.listbox.hidden = visible
    if (this.vlist) this.vlist.hidden = !visible
  }

  /** 虚拟滚动：让 activeIndex 所在项进入视口（设置 scrollTop，vlist 随 scroll 重算窗口） */
  private scrollActiveIntoView(): void {
    const vlist = this.vlist
    if (!vlist || vlist.hidden) return
    const ih = this.virtualItemHeight()
    const vp = vlist.shadowRoot?.querySelector<HTMLElement>('.viewport')
    if (!vp) return
    const top = this.activeIndex * ih
    const vh = vp.clientHeight || 240
    const cur = vp.scrollTop
    if (top < cur) vp.scrollTop = Math.max(0, top)
    else if (top + ih > cur + vh) vp.scrollTop = Math.max(0, top + ih - vh)
  }

  /** 高亮/aria 增量同步（不重建 DOM）：虚拟滚动下窗口随 scroll 重算后行内 class 由 createOptionRow 落定 */
  private syncActive(): void {
    for (const row of this.renderedOptionRows()) {
      const idx = Number(row.getAttribute('data-index'))
      row.classList.toggle('active', idx === this.activeIndex)
    }
    const visibleLen = this.visibleOptions().length
    const create = this.shadow.querySelector('.create-option')
    if (create) {
      create.classList.toggle('active', this.createVisible && this.activeIndex >= visibleLen)
    }
    this.syncAriaActiveDescendant()
  }

  /** 已渲染的选项行：非虚拟在 listbox、虚拟在 vlist shadow（open shadow 可跨根查询） */
  private renderedOptionRows(): HTMLElement[] {
    const out: HTMLElement[] = []
    const listbox = this.shadow.querySelector('.listbox')
    if (listbox) out.push(...listbox.querySelectorAll<HTMLElement>('.option[data-index]'))
    const vroot = this.vlist?.shadowRoot
    if (vroot) out.push(...vroot.querySelectorAll<HTMLElement>('.option[data-index]'))
    return out
  }

  /** combobox 的 aria-activedescendant 指向高亮行（仅展开时），随窗口滚动保持有效 */
  private syncAriaActiveDescendant(): void {
    if (!this.triggerEl) return
    if (!this.openState) {
      this.triggerEl.removeAttribute('aria-activedescendant')
      return
    }
    const n = this.navigableCount()
    if (n > 0 && this.activeIndex >= 0 && this.activeIndex < n) {
      this.triggerEl.setAttribute('aria-activedescendant', `opt-${this.activeIndex}`)
    } else {
      this.triggerEl.removeAttribute('aria-activedescendant')
    }
  }

  /** max-count 多选上限（仅 multiple 生效；未设置/非法值视为无上限；单选行为不变） */
  private maxCountLimit(): number | null {
    if (!this.hasAttr('multiple')) return null
    const n = this.intAttr('max-count')
    return n !== null && n > 0 ? n : null
  }

  /** 多选已达上限（当前选中数 >= max-count；已选项仍可取消，未选项禁止新增） */
  private limitReached(): boolean {
    const max = this.maxCountLimit()
    return max !== null && this.currentValues().length >= max
  }

  /** 上限拦截：超限时未选项的点击/键盘/创建一律拒绝并派发 oas-exceed-limit（detail: { value, max }） */
  private blockedByLimit(value: string): boolean {
    if (!this.limitReached() || this.currentValues().includes(value)) return false
    this.emit('exceed-limit', { value, max: this.maxCountLimit() })
    return true
  }

  /** remote 模式 oas-input 派发防抖窗口（ms；默认 0 立即派发不干扰现状） */
  private inputDebounce(): number {
    const n = this.intAttr('debounce')
    return n !== null && n > 0 ? n : 0
  }

  /** 值数组反查完整 option 对象（label/value/group/disabled 全量；未匹配为 null，宿主无需再反查） */
  private optionsOf(values: string[]): Array<Option | null> {
    return values.map((v) => this._options.find((o) => o.value === v) ?? null)
  }

  private selectValue(value: string): void {
    // 用户交互置脏：冻结 reset 基线（本组件交互会写回 value 属性，须与「受控写入刷新基线」区分）
    this.valueDirty = true
    if (this.hasAttr('multiple')) {
      const current = this.currentValues()
      const next = current.includes(value) ? current.filter((v) => v !== value) : [...current, value]
      this.setAttribute('value', JSON.stringify(next))
      this.emit('change', { value: next, options: this.optionsOf(next) })
    } else {
      this.setAttribute('value', value)
      this.emit('change', { value, option: this.optionsOf([value])[0] })
      this.requestOpen(false)
    }
    this.syncTrigger()
    // searchable 默认选中后清空搜索词（reserve-keyword 保留，对齐 tree-select 契约）；
    // 先清词再重渲染，列表恢复/保持对应状态
    if (this.hasAttr('searchable') && !this.hasAttr('reserve-keyword')) this.clearSearchQuery()
    this.renderListbox()
    // 值变化点同步原生表单数据 + 校验链（属性同值时无回调、不触发 update，这里兜底）
    this.syncFormValue()
    this.syncValidity()
  }

  /** 搜索词清空（选中后默认清空；reserve-keyword 保留）：搜索框值、查询镜像与 input-value 写回一并复位 */
  private clearSearchQuery(): void {
    const input = this.shadow.querySelector<HTMLInputElement>('.search-input')
    if (input) {
      input.value = ''
      input.setAttribute('data-query', '')
    }
    if (this.getAttribute('input-value') !== '') this.setAttribute('input-value', '')
  }

  /** clearable：清空值并派发 oas-clear（detail 为被清空前的值）+ oas-change（空值）；readonly 拦截（值只读不可改） */
  private clearValue(): void {
    if (this.injectDisabled() || this.hasAttr('readonly')) return
    // 用户交互置脏：清空后的选中集不回写 reset 基线（与原生「用户清空不改善通初始值」一致）
    this.valueDirty = true
    const prev = this.currentValues()
    if (this.hasAttr('multiple')) {
      this.setAttribute('value', '[]')
      this.emit('clear', { value: [...prev] })
      this.emit('change', { value: [], options: [] })
    } else {
      this.removeAttribute('value')
      this.emit('clear', { value: prev[0] ?? '' })
      this.emit('change', { value: '', option: null })
    }
    this.syncTrigger()
    this.renderListbox()
    // 值变化点同步原生表单数据 + 校验链（属性移除路径经 update 已同步，这里兜底）
    this.syncFormValue()
    this.syncValidity()
    this.triggerEl?.focus()
  }

  /** allow-create：以输入值创建选项并纳入选中（max-count 达上限时拦截并派发 oas-exceed-limit） */
  private createOption(): void {
    const label = this.createLabel.trim()
    if (label === '') return
    if (this.blockedByLimit(label)) return
    // 创建项进持久层（parseOptions 每轮重解析源数据，直接 push 会被下一轮抹掉）
    if (!this.createdOptions.some((c) => c.value === label)) {
      this.createdOptions.push({ label, value: label })
    }
    this.createVisible = false
    // 新选项创建即派发 oas-create（detail 携带创建文本；随后正常走选中/变更链路）
    this.emit('create', { value: label, label })
    this.selectValue(label)
  }

  private currentValues(): string[] {
    const raw = this.getAttr('value', this.hasAttr('multiple') ? '[]' : '')
    if (this.hasAttr('multiple')) {
      try {
        const parsed = JSON.parse(raw)
        return Array.isArray(parsed) ? parsed.filter((v) => typeof v === 'string') : []
      } catch {
        return []
      }
    }
    return raw === '' ? [] : [raw]
  }

  private parseOptions(): void {
    // 双通道：options 属性显式设置时数据驱动优先；否则解析子元素收敛到同一 options 模型渲染。
    // 收敛点在虚拟/非虚拟两条渲染路径之前的唯一数据入口，两路径都吃到子元素通道数据
    let base: Option[]
    if (this.hasAttribute('options')) {
      try {
        const parsed = JSON.parse(this.getAttr('options', '[]'))
        base = Array.isArray(parsed) ? parsed.filter((o): o is Option => o && typeof o.value === 'string') : []
      } catch {
        base = []
      }
    } else {
      base = this.parseChildOptions()
    }
    // allow-create 创建的选项持久（源数据刷新/重渲染不丢失，已选 label 与 detail.option 可反查）；
    // 源数据后来补上同值选项时以源为准（去重）
    const created = this.createdOptions.filter((c) => !base.some((b) => b.value === c.value))
    this._options = [...base, ...created]
  }

  // ===== 子元素声明式通道 =====

  /** 解析 light DOM 的 `<oas-option>` 数据载体为 Option[]（其余 light DOM 内容忽略） */
  private parseChildOptions(): Option[] {
    const options: Option[] = []
    for (const child of Array.from(this.children)) {
      if (child.tagName === 'OAS-OPTION') options.push(this.childToOption(child))
    }
    return options
  }

  /** 单个 <oas-option> → Option（默认插槽文本为 label，属性对齐 options 字段） */
  private childToOption(el: Element): Option {
    const option: Option = { label: this.childLabel(el), value: el.getAttribute('value') ?? '' }
    if (el.hasAttribute('disabled')) option.disabled = true
    const group = el.getAttribute('group')
    if (group) option.group = group
    return option
  }

  /** 默认插槽 label 文本（trim） */
  private childLabel(el: Element): string {
    let text = ''
    for (const node of el.childNodes) {
      text += node.textContent ?? ''
    }
    return text.trim()
  }

  /**
   * 子元素通道观察器：只监听 light DOM 子元素（数据载体增删/属性/文本变化 → 重解析）。
   * 组件自身动作不写 light DOM，无需自引用守卫。
   */
  private ensureChildObserver(): void {
    if (this.childObserver) return
    const observer = new MutationObserver(() => {
      this.update()
    })
    observer.observe(this, {
      childList: true,
      subtree: true,
      attributes: true,
      characterData: true,
      attributeFilter: ['value', 'disabled', 'group', 'slot'],
    })
    this.childObserver = observer
    this.onCleanup(() => {
      observer.disconnect()
      this.childObserver = null
    })
  }

  private intAttr(name: string): number | null {
    const raw = this.getAttr(name, '').trim()
    if (raw === '') return null
    const n = Number.parseInt(raw, 10)
    return Number.isNaN(n) ? null : n
  }

  /** 单行（max-tag-count 折叠模式）放不下时把放不下的标签收进 +N（不换行、不出横向滚动条） */
  private collapseOverflowChips(
    valueEl: HTMLElement,
    plus: HTMLElement,
    allLabels: string[],
    renderedCount: number,
  ): void {
    const chips = [...valueEl.querySelectorAll<HTMLElement>('.chip:not(.chip-plus)')]
    let hidden = allLabels.length - renderedCount // 已按 max-tag-count 折叠的数量
    // 无需折叠且单行放得下：移除 +N，不占位
    if (hidden === 0 && valueEl.scrollWidth <= valueEl.clientWidth) {
      plus.remove()
      return
    }
    const applyPlus = () => {
      plus.hidden = false
      plus.textContent = `+${hidden}`
      plus.setAttribute('title', allLabels.slice(allLabels.length - hidden).join('、'))
    }
    applyPlus()
    // 从后往前收起，直到单行放得下
    for (let i = chips.length - 1; i >= 0; i--) {
      if (valueEl.scrollWidth <= valueEl.clientWidth) break
      const c = chips[i]
      if (!c) continue
      c.hidden = true
      hidden++
      applyPlus()
    }
  }

  private syncTrigger(): void {
    if (!this.triggerEl) return
    const placeholder = this.getAttr('placeholder', this.t('select.placeholder'))
    // disabled 就近读取全局禁用注入（组件显式 disabled > 豁免 > provider 注入）
    const disabled = this.injectDisabled()
    // readonly 与 disabled 表单语义分立：可聚焦可复制、不弹层、值不可改
    const readonly = this.hasAttr('readonly')
    const values = this.currentValues()
    const valueEl = this.triggerEl.querySelector<HTMLElement>('.value')!

    this.triggerEl.disabled = disabled
    // aria-label：label 属性（表单标签，对齐 oas-input 契约）> placeholder 回退；
    // 外部 <label for> 关联命名由基类 syncInnerLabelledBy 在 update 后覆盖（外部 label 优先）
    this.triggerEl.setAttribute('aria-label', this.getAttr('label', '') || placeholder)
    if (readonly) this.triggerEl.setAttribute('aria-readonly', 'true')
    else this.triggerEl.removeAttribute('aria-readonly')

    // 清空按钮：clearable && 有值 && 未禁用 && 非只读 时显示
    const clearBtn = this.shadow.querySelector<HTMLButtonElement>('.clear-btn')
    if (clearBtn) {
      clearBtn.hidden = !(this.hasAttr('clearable') && !disabled && !readonly && values.length > 0)
      clearBtn.setAttribute('aria-label', this.t('input.clear'))
    }

    if (values.length === 0) {
      valueEl.innerHTML = ''
      const ph = document.createElement('span')
      ph.className = 'placeholder'
      ph.textContent = placeholder
      valueEl.appendChild(ph)
      return
    }

    if (this.hasAttr('multiple')) {
      valueEl.innerHTML = ''
      // max-tag-count：仅显式设置时才按数量折叠；未设置时标签默认换行展示（flex-wrap: wrap 见 CSS）
      const raw = this.hasAttr('max-tag-count') ? this.intAttr('max-tag-count') : null
      const limit = raw !== null && raw >= 0 ? raw : Number.POSITIVE_INFINITY
      const shown = values.slice(0, limit)
      const allLabels = values.map((v) => this._options.find((o) => o.value === v)?.label ?? v)
      for (const v of shown) {
        const option = this._options.find((o) => o.value === v)
        const label = option?.label ?? v
        const chip = document.createElement('span')
        chip.className = 'chip'
        const labelEl = document.createElement('span')
        this.fillTagLabel(labelEl, v, label)
        chip.append(labelEl)
        // readonly：值只读——chip 不带移除按钮（与清空按钮一致的分立语义）
        if (!readonly) {
          const rm = document.createElement('button')
          rm.setAttribute('aria-label', this.t('select.remove', { label }))
          rm.textContent = '×'
          rm.addEventListener('click', (e: MouseEvent) => {
            e.stopPropagation()
            this.selectValue(v)
          })
          chip.append(rm)
        }
        valueEl.appendChild(chip)
      }
      // 折叠计数 chip：仅在显式设置 max-tag-count 时插入（数量折叠 + 超宽折叠合并计数）
      if (this.hasAttr('max-tag-count')) {
        const plus = document.createElement('span')
        plus.className = 'chip chip-plus'
        valueEl.appendChild(plus)
        this.collapseOverflowChips(valueEl, plus, allLabels, shown.length)
      }
    } else {
      const value = values[0] ?? ''
      const option = this._options.find((o) => o.value === value)
      valueEl.textContent = option?.label ?? value
    }
  }

  /** 标签（chip）渲染：template[slot="tag"] 克隆 + [data-tag-label] 绑定；随后派发 oas-tag-render 供宿主改写 */
  private fillTagLabel(labelEl: HTMLElement, value: string, label: string): void {
    const tpl = this.querySelector('template[slot="tag"]')
    if (tpl instanceof HTMLTemplateElement) {
      labelEl.innerHTML = ''
      labelEl.appendChild(tpl.content.cloneNode(true))
      const binder = labelEl.querySelector('[data-tag-label]')
      if (binder) binder.textContent = label
      labelEl.classList.add('chip-label')
    } else {
      labelEl.textContent = label
    }
    this.emit('tag-render', { value, label, element: labelEl })
  }

  /**
   * 表单值快照（form-associated）：
   * - 单选：选中值字符串（value 属性；无选中 → null，FormData 不含此项）
   * - 多选：原生「同名多条」语义——返回含多条同名 entry 的 FormData（key 取 name 属性），
   *   无选中 → null；**无 name 亦返回 null**——FormData 通道由组件自建 entry，不受浏览器
   *   「无 name 不提交」兜底保护，须显式拦下避免空名 entry（对齐 combobox 多选同款拦截）
   */
  protected override getFormValue(): string | FormData | null {
    const values = this.currentValues()
    if (values.length === 0) return null
    if (!this.hasAttr('multiple')) return values[0] ?? null
    const name = this.getAttr('name', '')
    if (name === '') return null
    const fd = new FormData()
    for (const v of values) fd.append(name, v)
    return fd
  }

  /** 原生校验链同步：required 且无选中 → valueMissing（flag 为 true 时 message 按 Chromium 契约必须非空） */
  private syncValidity(): void {
    // 多选不能用 getFormValue()===null 作判据：无 name 时它恒为 null，会把「有选中」误判
    // valueMissing——多选按选中集是否为空判定（对齐 combobox 同款分流）
    const missing = this.hasAttr('multiple') ? this.currentValues().length === 0 : this.getFormValue() === null
    if (this.hasAttr('required') && missing) {
      this.setValidity({ valueMissing: true }, this.t('form.valueMissing'))
    } else {
      this.setValidity({})
    }
  }

  /** 表单 reset：恢复初始选中基线并清脏（受控源为 value 属性），不派发事件（与原生 reset 一致） */
  protected override resetFormValue(): void {
    this.valueDirty = false
    const baseline = this.initialValue
    if (baseline.length === 0) {
      this.removeAttribute('value')
    } else if (this.hasAttr('multiple')) {
      this.setAttribute('value', JSON.stringify(baseline))
    } else {
      this.setAttribute('value', baseline[0] ?? '')
    }
    // 属性同值/缺席时无回调、不触发 update，UI 兜底刷新（触发器显隐与下拉选中态）
    this.syncTrigger()
    this.renderListbox()
    this.syncValidity()
  }

  /** shadow 内真实焦点目标：触发器 button。基类默认选择器会误中隐藏搜索框/清除钮（tabindex=-1），必须覆盖 */
  protected override get innerControl(): HTMLElement | null {
    return this.triggerEl
  }

  /** label 点击聚焦委托：把焦点交给 shadow 内 trigger（配合 oas-form-item 的 label 点击代理） */
  override focus(options?: FocusOptions): void {
    this.shadow.querySelector<HTMLButtonElement>('.trigger')?.focus(options)
  }
}
